import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminStore } from '../../store/adminStore'
import Spinner from '../../components/common/Spinner'
import type { CrowdTask } from '../../types'



const STATUS_CONFIG: Record<string, { bg: string; color: string; label: string }> = {
  draft:         { bg: '#6b728022', color: '#9ca3af', label: 'Draft' },
  published:     { bg: '#22c55e22', color: '#22c55e', label: 'Published' },
  fully_claimed: { bg: '#f59e0b22', color: '#f59e0b', label: 'Full' },
  completed:     { bg: '#3b82f622', color: '#3b82f6', label: 'Done' },
  expired:       { bg: '#ef444422', color: '#ef4444', label: 'Expired' },
  cancelled:     { bg: '#6b728022', color: '#9ca3af', label: 'Cancelled' },
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl p-3" style={{ background: 'var(--app-card-bg)' }}>
      <p className="text-2xl font-bold" style={{ color: 'var(--tg-theme-text-color)' }}>
        {value}
      </p>
      <p className="text-xs opacity-50 mt-0.5" style={{ color: 'var(--tg-theme-text-color)' }}>
        {label}
      </p>
    </div>
  )
}

function TaskRow({ task, onPublish }: { task: CrowdTask; onPublish: (id: string) => void }) {
  const navigate = useNavigate()
  const s = STATUS_CONFIG[task.status] ?? STATUS_CONFIG.draft
  const expiresDate = new Date(task.expires_at)
  const isExpired = expiresDate < new Date()

  return (
    <div
      className="rounded-xl p-3 space-y-2 cursor-pointer active:opacity-70 transition-opacity"
      style={{ background: 'var(--app-card-bg)' }}
      onClick={() => navigate(`/admin/tasks/${task.id}`)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {(task.header_image_url || task.gallery_images?.[0]?.image_url) && (
            <img
              src={task.header_image_url ?? task.gallery_images![0].image_url}
              alt=""
              className="rounded-lg object-cover flex-shrink-0"
              style={{ width: 40, height: 40 }}
            />
          )}
          <div className="flex-1 min-w-0">
            <p
              className="font-medium text-sm"
              style={{ color: 'var(--tg-theme-text-color)' }}
            >
              {task.title}
            </p>
            {task.target_location_name && (
              <p className="text-xs opacity-50 mt-0.5" style={{ color: 'var(--tg-theme-text-color)' }}>
                📍 {task.target_location_name}
              </p>
            )}
          </div>
        </div>
        <span
          className="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
          style={{ background: s.bg, color: s.color }}
        >
          {s.label}
        </span>
      </div>

      <div
        className="flex items-center gap-3 text-xs opacity-50"
        style={{ color: 'var(--tg-theme-text-color)' }}
      >
        <span>{task.items.length} item{task.items.length !== 1 ? 's' : ''}</span>
        <span>{task.current_claims}/{task.max_claims} claimed</span>
        <span className={isExpired && task.status === 'published' ? 'text-red-400' : ''}>
          {isExpired ? 'expired' : `expires ${expiresDate.toLocaleDateString()}`}
        </span>
      </div>

      {task.status === 'draft' && (
        <button
          className="w-full py-1.5 rounded-lg text-xs font-semibold"
          style={{ background: '#22c55e', color: 'white' }}
          onClick={(e) => {
            e.stopPropagation()
            onPublish(task.id)
          }}
        >
          Publish →
        </button>
      )}
    </div>
  )
}

export default function AdminTasksPage() {
  const { tasks, stats, isLoading, error, fetchTasks, fetchStats, publishTask } = useAdminStore()
  const navigate = useNavigate()

  useEffect(() => {
    fetchTasks()
    fetchStats()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handlePublish = async (taskId: string) => {
    try {
      await publishTask(taskId)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to publish')
    }
  }

  const totalTasks = stats
    ? Object.values(stats.tasks_by_status).reduce((a, b) => a + b, 0)
    : 0

  return (
    <div className="p-4 space-y-3 pb-8">
      {/* Stats grid */}
      {stats && (
        <div className="grid grid-cols-2 gap-2">
          <StatCard label="Active workers" value={stats.active_workers} />
          <StatCard label="Assignments done" value={stats.completed_assignments} />
          <StatCard label="Pending reviews" value={stats.submissions_by_status?.pending ?? 0} />
          <StatCard label="Total tasks" value={totalTasks} />
        </div>
      )}

      {/* Task status breakdown */}
      {stats && totalTasks > 0 && (
        <div className="flex gap-1.5 flex-wrap">
          {Object.entries(stats.tasks_by_status).map(([status, count]) => {
            const s = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft
            return (
              <span
                key={status}
                className="text-xs px-2 py-0.5 rounded-full"
                style={{ background: s.bg, color: s.color }}
              >
                {s.label}: {count}
              </span>
            )
          })}
        </div>
      )}

      {/* Create button */}
      <button
        className="w-full py-2.5 rounded-xl text-sm font-semibold"
        style={{
          background: 'var(--tg-theme-button-color)',
          color: 'var(--tg-theme-button-text-color)',
        }}
        onClick={() => navigate('/admin/tasks/new')}
      >
        + New Task
      </button>

      {/* List */}
      {isLoading && <Spinner />}
      {error && (
        <p className="text-sm text-center py-4" style={{ color: '#ef4444' }}>
          {error}
        </p>
      )}
      {!isLoading && tasks.length === 0 && !error && (
        <p
          className="text-sm text-center opacity-40 py-10"
          style={{ color: 'var(--tg-theme-text-color)' }}
        >
          No tasks yet — create one above
        </p>
      )}
      <div className="space-y-2">
        {tasks.map((task) => (
          <TaskRow key={task.id} task={task} onPublish={handlePublish} />
        ))}
      </div>
    </div>
  )
}
